import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import { Storage } from '../utils/storage.ts'
import { resolveTenant } from '../api/tenant'

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    redirect: () => {
      const tenantId = Storage.getTenantId()
      return tenantId ? '/school' : '/platform'
    }
  },
  // --- 登录注册独立布局 ---
  {
    path: '/login',
    name: 'Login',
    component: () => import('../views/auth/Login.vue')
  },
  {
    path: '/register',
    name: 'Register',
    component: () => import('../views/auth/Register.vue')
  },
  // --- 平台端 ---
  {
    path: '/platform',
    component: () => import('../layouts/PlatformLayout.vue'),
    children: [
      {
        path: '',
        name: 'PlatformHome',
        component: () => import('../views/platform/Home.vue')
      },
      {
        path: 'courses',
        name: 'PlatformCourses',
        component: () => import('../views/platform/courses/Index.vue')
      },
      {
        path: 'courses/:id',
        name: 'PlatformCourseDetail',
        component: () => import('../views/platform/courses/Detail.vue')
      },
      {
        path: 'courses/:id/study',
        name: 'PlatformCourseStudy',
        component: () => import('../views/platform/courses/Study.vue')
      },
      {
        path: 'resources',
        name: 'PlatformResources',
        component: () => import('../views/platform/resources/Index.vue')
      },
      {
        path: 'resources/:id',
        name: 'PlatformResourcePreview',
        component: () => import('../views/school/resources/Preview.vue')
      },
      {
        path: 'schools',
        name: 'PlatformSchools',
        component: () => import('../views/platform/schools/Index.vue')
      },
      {
        path: 'rooms',
        name: 'PlatformRooms',
        component: () => import('../views/platform/rooms/Index.vue')
      },
      {
        path: 'activities',
        name: 'PlatformActivities',
        component: () => import('../views/platform/activities/Index.vue')
      },
      {
        path: 'activities/:id',
        name: 'PlatformActivityDetail',
        component: () => import('../views/platform/activities/Detail.vue')
      },
      {
        path: 'ai',
        name: 'PlatformAI',
        component: () => import('../views/school/ai/Index.vue') // 复用 AI 助教组件
      },
      {
        path: 'ai-workbench',
        name: 'PlatformAIWorkbench',
        component: () => import('../views/school/ai/Workbench.vue')
      },
      {
        path: 'ai-lesson-plan',
        name: 'PlatformAiLessonPlan',
        component: () => import('../views/school/ai/AiLessonPlan.vue')
      },
      {
        path: 'ai-ideological-case',
        name: 'PlatformAiIdeologicalCase',
        component: () => import('../views/school/ai/AiIdeologicalCase.vue')
      },
      {
        path: 'ai-question-bank',
        name: 'PlatformAiQuestionBank',
        component: () => import('../views/school/ai/AiQuestionBank.vue')
      },
      {
        path: 'ai-manuscript',
        name: 'PlatformAiManuscript',
        component: () => import('../views/school/ai/AiManuscript.vue')
      },
      {
        path: 'notices',
        name: 'PlatformNotices',
        component: () => import('../views/platform/notices/Index.vue')
      },
      {
        path: 'notices/:id',
        name: 'PlatformNoticeDetail',
        component: () => import('../views/platform/notices/Detail.vue')
      },
      {
        path: 'profile',
        name: 'PlatformProfile',
        component: () => import('../views/school/profile/Index.vue')
      },
    ]
  },
  // --- 学校端 ---
  {
    path: '/school',
    component: () => import('../layouts/SchoolLayout.vue'),
    children: [
      {
        path: '',
        name: 'SchoolHome',
        component: () => import('../views/school/Home.vue')
      },
      {
        path: 'statistics',
        name: 'SchoolStatistics',
        component: () => import('../views/school/SchoolStatistics.vue')
      },
      {
        path: 'rooms',
        name: 'SchoolRooms',
        component: () => import('../views/school/rooms/Index.vue')
      },
      {
        path: 'notices',
        name: 'SchoolNotices',
        component: () => import('../views/school/notices/Index.vue')
      },
      {
        path: 'notices/:id',
        name: 'SchoolNoticeDetail',
        component: () => import('../views/school/notices/Detail.vue')
      },
      {
        path: 'courses',
        name: 'SchoolCourses',
        component: () => import('../views/platform/courses/Index.vue') // 复用平台端通识课程组件
      },
      {
        path: 'courses/:id',
        name: 'SchoolCourseDetail',
        component: () => import('../views/platform/courses/Detail.vue')
      },
      {
        path: 'courses/:id/study',
        name: 'SchoolCourseStudy',
        component: () => import('../views/platform/courses/Study.vue')
      },
      {
        path: 'resources',
        name: 'SchoolResources',
        component: () => import('../views/school/resources/Index.vue')
      },
      {
        path: 'resources/:id',
        name: 'SchoolResourcePreview',
        component: () => import('../views/school/resources/Preview.vue')
      },
      {
        path: 'calendar',
        name: 'SchoolCalendar',
        component: () => import('../views/school/calendar/Index.vue')
      },
      {
        path: 'activity/:activityId',
        name: 'SchoolActivityDetail',
        component: () => import('../views/school/room-detail/ActivityDetail.vue')
      },
      {
        path: 'activity/:activityId/chat',
        name: 'SchoolActivityChat',
        component: () => import('../views/school/room-detail/ActivityChat.vue')
      },
      // 个人中心相关
      {
        path: 'profile',
        name: 'SchoolProfile',
        component: () => import('../views/school/profile/Index.vue')
      },
      {
        path: 'my-rooms',
        name: 'SchoolMyRooms',
        component: () => import('../views/school/my-rooms/Index.vue')
      },
      {
        path: 'my-resources',
        name: 'SchoolMyResources',
        component: () => import('../views/school/my-resources/Index.vue')
      },
      {
        path: 'ai',
        name: 'SchoolAI',
        component: () => import('../views/school/ai/Index.vue')
      },
      {
        path: 'ai-workbench',
        name: 'SchoolAIWorkbench',
        component: () => import('../views/school/ai/Workbench.vue')
      },
      {
        path: 'ai-workbench-home',
        name: 'SchoolAIWorkbenchHome',
        component: () => import('../views/school/ai/WorkbenchHome.vue')
      },
      {
        path: 'ai-lesson-plan',
        name: 'SchoolAiLessonPlan',
        component: () => import('../views/school/ai/AiLessonPlan.vue')
      },
      {
        path: 'ai-ideological-case',
        name: 'SchoolAiIdeologicalCase',
        component: () => import('../views/school/ai/AiIdeologicalCase.vue')
      },
      {
        path: 'ai-question-bank',
        name: 'SchoolAiQuestionBank',
        component: () => import('../views/school/ai/AiQuestionBank.vue')
      },
      {
        path: 'ai-manuscript',
        name: 'SchoolAiManuscript',
        component: () => import('../views/school/ai/AiManuscript.vue')
      },
      // 教研室管理后台
      {
        path: 'room/:id/manage',
        name: 'RoomManage',
        component: () => import('../views/school/room-manage/Index.vue'),
        redirect: to => {
          return { path: `/school/room/${to.params.id}/manage/overview` }
        },
        children: [
          {
            path: 'overview',
            name: 'RoomManageOverview',
            component: () => import('../views/school/room-manage/RoomManageOverview.vue')
          },
          {
            path: 'applications',
            name: 'RoomManageApplications',
            component: () => import('../views/school/room-manage/RoomManageApplications.vue')
          },
          {
            path: 'members',
            name: 'RoomManageMembers',
            component: () => import('../views/school/room-manage/RoomManageMembers.vue')
          },
          {
            path: 'activities',
            name: 'RoomManageActivities',
            component: () => import('../views/school/room-manage/RoomManageActivities.vue')
          },
          {
            path: 'meeting-tasks',
            name: 'RoomManageMeetingTasks',
            component: () => import('../views/school/room-manage/RoomManageMeetingTasks.vue')
          },
          {
            path: 'announcements',
            name: 'RoomManageAnnouncements',
            component: () => import('../views/school/room-manage/RoomManageAnnouncements.vue')
          },
          {
            path: 'ai-vector',
            name: 'RoomManageAiVector',
            component: () => import('../views/school/room-manage/RoomManageAiVector.vue')
          }
        ]
      },
      // 学校管理（个人中心入口）
      {
        path: 'school-manage',
        name: 'SchoolManage',
        component: () => import('../views/school/school-manage/Index.vue'),
        redirect: '/school/school-manage/organizations',
        children: [
          {
            path: 'organizations',
            name: 'SchoolManageOrganizations',
            component: () => import('../views/school/school-manage/Organizations.vue')
          },
          {
            path: 'roles',
            name: 'SchoolManageRoles',
            component: () => import('../views/school/school-manage/Roles.vue')
          },
          {
            path: 'permissions',
            name: 'SchoolManagePermissions',
            component: () => import('../views/school/school-manage/Permissions.vue')
          },
          {
            path: 'staff',
            name: 'SchoolManageStaff',
            component: () => import('../views/school/school-manage/Staff.vue')
          },
          {
            path: 'rooms',
            name: 'SchoolManageRooms',
            component: () => import('../views/school/school-manage/Rooms.vue')
          },
          {
            path: 'wemeet-accounts',
            name: 'SchoolManageWeMeetAccounts',
            component: () => import('../views/school/school-manage/WeMeetAccounts.vue')
          }
        ]
      },
      // 核心：教研室详情大页
      {
        path: 'room/:id',
        name: 'RoomDetail',
        component: () => import('../views/school/room-detail/Index.vue'),
        redirect: to => {
          return { path: `/school/room/${to.params.id}/home` }
        },
        children: [
          {
            path: 'home',
            name: 'RoomDetailHome',
            component: () => import('../views/school/room-detail/RoomHome.vue')
          },
          {
            path: 'resources',
            name: 'RoomDetailResources',
            component: () => import('../views/school/room-detail/RoomResources.vue')
          },
          {
            path: 'resource/:resourceId',
            name: 'RoomResourcePreview',
            component: () => import('../views/school/resources/Preview.vue')
          },
          {
            path: 'activities',
            name: 'RoomDetailActivities',
            component: () => import('../views/school/room-detail/RoomActivities.vue')
          },
          {
            path: 'activity/:activityId',
            name: 'RoomActivityDetail',
            component: () => import('../views/school/room-detail/ActivityDetail.vue')
          },
          {
            path: 'activity/:activityId/chat',
            name: 'RoomActivityChat',
            component: () => import('../views/school/room-detail/ActivityChat.vue')
          },
          {
            path: 'discussions',
            name: 'RoomDetailDiscussions',
            component: () => import('../views/school/room-detail/RoomDiscussions.vue')
          },
          {
            path: 'notices',
            name: 'RoomDetailNotices',
            component: () => import('../views/school/room-detail/RoomNotices.vue')
          },
          {
            path: 'notice/:noticeId',
            name: 'RoomNoticeDetail',
            component: () => import('../views/school/room-detail/NoticeDetail.vue')
          },
          {
            path: 'members',
            name: 'RoomDetailMembers',
            component: () => import('../views/school/room-detail/RoomMembers.vue')
          },
          {
            path: 'statistics',
            name: 'RoomDetailStatistics',
            component: () => import('../views/school/room-detail/RoomStatistics.vue')
          }
        ]
      }
    ]
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior() {
    return { top: 0 }
  }
})

// 移除默认在 main.ts 中强制 window.location.href 跳转的逻辑，改在 router.beforeEach 处理
// 注意：由于 matchedTenantId 是在 main.ts 启动时异步获取的，建议依赖 Storage 的标志判断
router.beforeEach(async (to) => {
  let tenantId = Storage.getTenantId()
  if (!tenantId) {
    try {
      const res: any = await resolveTenant(window.location.host)
      const matched = String(res?.data?.tenantId || '')
      if (matched) {
        Storage.setTenantId(matched)
        tenantId = matched
      }
    } catch {
    }
  }

  if (to.path === '/login' || to.path === '/register') {
    return true
  }
  
  // 匹配到学校独立站点：平台端路由全部强制进入学校端
  if (tenantId && to.path.startsWith('/platform')) {
    return '/school'
  }
  
  // 其他情况正常放行
  return true
})

export default router
